import type { Mutation } from '@tanstack/react-query'
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'

import { ApiError, clearToken } from './api'

declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: {
      skipAuthRedirect?: boolean
    }
  }
}

/** Retrying a 4xx never succeeds — only network failures and 5xx are worth a
 * second attempt. */
function shouldRetry(failureCount: number, error: unknown): boolean {
  if (failureCount >= 2) return false
  if (error instanceof ApiError) {
    return error.isNetworkError || error.status >= 500
  }
  return false
}

/** An expired/invalid admin token can surface from any query or mutation, so
 * it is handled once here rather than at every call site. Full document load
 * on redirect (not client-side nav) guarantees no stale authenticated state
 * survives into the signed-out app. */
let redirecting = false

function handleAuthError(error: unknown, mutation?: Mutation<unknown, unknown, unknown>) {
  if (!(error instanceof ApiError) || !error.isAuthError) return
  if (mutation?.options.meta?.skipAuthRedirect) return
  if (redirecting) return
  redirecting = true
  clearToken()
  window.location.assign('/login')
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: (error) => handleAuthError(error) }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _onMutateResult, mutation) => handleAuthError(error, mutation),
  }),
  defaultOptions: {
    queries: {
      retry: shouldRetry,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
    mutations: {
      retry: false,
    },
  },
})
